import type { ChangeEvent, ReactNode, RefObject } from "react";
import { SignatureModal } from "../../../components/Form/SignatureModel";
import type { LegalAdviceFormOneData } from "../../../types/LegalAdvices/LegalAdviceFormOne.type";
import { DatalistInput } from "../components/dataListInput";
// import { FormActionButtons } from "../../../components/Form/FormActionButton";
import { useSettings } from "../../../context/SettingContext";


export interface AttachmentFile {
  name: string;
  [key: string]: any;
}

export interface DetailFormDocumentProps {
  formData: LegalAdviceFormOneData;
  isEditable: boolean;
  isSigning: boolean;
  sigCanvasRef: RefObject<any>;
  setIsSigning: (isSigning: boolean) => void;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSaveSignature: () => void;
  renderDocumentRow: (label: string, fieldName: any) => ReactNode;
  renderDatePicker: (fieldName: any) => ReactNode;

  // Additional props required for action controls inside this component
  isPreview?: boolean;
  // isSubmitting?: boolean;
  // isApproved?: boolean;
  handleBackNavigation: () => void;
  // handleSaveDraft: (e: React.MouseEvent) => void;
  // handleSubmitToSuperior: (e: React.MouseEvent) => void;
 // handleAttachment:(e:React.MouseEvent)=> void;
}

export const LegalDetailFormOneDocument: React.FC<DetailFormDocumentProps> = ({
  formData,
  isEditable,
  isSigning,
  sigCanvasRef,
  setIsSigning,
  handleChange,
  handleSaveSignature,
  renderDatePicker,
  // isApproved,
  // isSubmitting = false,
  // handleBackNavigation,
  // handleSaveDraft,
  // handleSubmitToSuperior,
  // handleAttachment
}) => {
  // Replace these with your actual option data sources or imports
 const { settings } = useSettings();

//  const isFormSigned = Boolean(
//     formData.signatureImage?.trim() && 
//     (formData.signerName?.trim() || formData.signName?.trim())
//   );

// Map to simple string arrays for your dropdowns/datalists
const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];


  return (
    /* A4 Sheet Container */
    <div className="w-full max-w-[800px] bg-white shadow-lg border border-gray-300 p-12 text-black leading-relaxed relative text-sm md:text-base print:p-0 print:border-none print:shadow-none">
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs text-gray-400 print:top-0 print:left-1/2">
        လျှို့ဝှက်
      </div>
      <div className="absolute top-4 right-4 text-xs font-bold print:top-0 print:right-0">
        ဥပရအမှုလိုက်ပုံစံ (၁)
      </div>

      {/* Header Section */}
      <div className="text-center space-y-2 mt-4">
        <h1 className="text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>

        {/* ရုံး */}
        <div className="flex justify-center items-center gap-2">
          <DatalistInput
            name="office"
            value={formData.office || ""}
            onChange={handleChange}
            placeholder=""
            options={OFFICE_OPTIONS}
            disabled={!isEditable}
          />
          <span className="w-24 text-left">ရုံး</span>
        </div>

        {/* မြို့နယ် */}
        <div className="flex justify-center items-center gap-2">
          <DatalistInput
            name="township"
            value={formData.township || ""}
            onChange={handleChange}
            placeholder=""
            options={TOWNSHIP_OPTIONS}
            disabled={!isEditable}
          />
          <span className="w-24 text-left">မြို့နယ်</span>
        </div>
      </div>

      {/* Letter No & Date Meta Section */}
      <div className="flex flex-col items-end space-y-2 mt-6">
        <div className="flex items-center gap-1 w-64 justify-end">
          <span className="whitespace-nowrap">စာအမှတ်၊</span>
          <input
            type="text"
            name="letterNo"
            value={formData.letterNo || ""}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
          />
        </div>

        <div className="flex items-center gap-2 w-64 justify-end">
          <span className="whitespace-nowrap">ရက်စွဲ၊</span>
          <div className="flex-grow">{renderDatePicker("date")}</div>
        </div>
      </div>

      {/* Content Section */}
      <div className="mt-8 space-y-6">
        <div className="flex items-start gap-2">
          <span className="font-bold whitespace-nowrap">အကြောင်းအရာ။</span>
          <p className="font-bold underline-offset-4">
            ပြစ်မှုဆိုင်ရာအမှုများကို တရားစွဲဆိုခြင်းမပြုမီ ဥပဒေအကြံပေးခြင်း
          </p>
        </div>

        {/* ရည်ညွှန်းချက် */}
        <div className="space-y-3">
          <span className="font-bold block">ရည်ညွှန်းချက်။</span>

          {/* ရည်ညွှန်းချက် (၁) */}
          <div className="pl-6 text-justify leading-8">
            (၁){" "}
            <DatalistInput
              name="refPoliceStation"
              value={formData.refPoliceStation || ""}
              onChange={handleChange}
              placeholder=""
              options={OFFICE_OPTIONS}
              disabled={!isEditable}
              className="w-36 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            />
            ရဲစခန်း၊ (ပ) အမှတ်{" "}
            <input
              type="text"
              name="refNo"
              value={formData.refNo || ""}
              onChange={handleChange}
              disabled={!isEditable}
              className="w-24 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            />
            တရားလို{" "}
            <input
              type="text"
              name="complainant"
              value={formData.complainant || ""}
              onChange={handleChange}
              disabled={!isEditable}
              className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            />
            နှင့် တရားခံ{" "}
            <input
              type="text"
              name="accused"
              value={formData.accused || ""}
              onChange={handleChange}
              disabled={!isEditable}
              className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            />
            ၊ ဥပဒေပုဒ်မ{" "}
            <input
              type="text"
              name="lawSection"
              value={formData.lawSection || ""}
              onChange={handleChange}
              disabled={!isEditable}
              className="w-28 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            />
            အရေးယူထားသော ရဲစက္ကူအမှုတွဲ။
          </div>

          {/* ရည်ညွှန်းချက် (၂) */}
          <div className="pl-6 text-justify leading-8">
            (၂){" "}
            <DatalistInput
              name="policeoffice"
              value={formData.policeoffice || ""}
              onChange={handleChange}
              placeholder=""
              options={OFFICE_OPTIONS}
              disabled={!isEditable}
              className="w-48 bg-transparent border-b border-black focus:outline-none py-0.5 text-center mx-1"
            />
            ရဲတပ်ဖွဲ့မှူးရုံး၏{" "}
            <div className="flex-grow">{renderDatePicker("refLetterDate")}</div>
            ရက်စွဲပါစာအမှတ်{" "}
            <input
              type="text"
              name="refLetterNo"
              value={formData.refLetterNo || ""}
              onChange={handleChange}
              disabled={!isEditable}
              className="w-32 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
            />
          </div>
        </div>

        <hr className="border-black my-4" />

        {/* Form Content Body */}
        <div className="space-y-6">
          <div>
            <span className="font-bold">၁။ အမှုအကြောင်းအရာ အကျဉ်းချုပ်</span>
            <textarea
              name="summary"
              value={formData.summary || ""}
              onChange={handleChange}
              disabled={!isEditable}
              rows={3}
              className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
            />
          </div>

          <div>
            <span className="font-bold">၂။ လေ့လာတွေ့ရှိချက်များ</span>
            <div className="pl-6 mt-2 space-y-4">
              <div>
                <span>(က) တရားလိုနှင့် သက်သေထွက်ဆိုချက်များ အကျဉ်း</span>
                <textarea
                  name="witnessStatement"
                  value={formData.witnessStatement || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  rows={2}
                  className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                />
              </div>
              <div>
                <span>(ခ) တရားခံထွက်ဆိုချက်အကျဉ်း</span>
                <textarea
                  name="accusedStatement"
                  value={formData.accusedStatement || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  rows={2}
                  className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                />
              </div>
              <div>
                <span>(ဂ) သိမ်းဆည်းရမိသော သက်သေခံပစ္စည်းများနှင့် စာရွက်စာတမ်းများ</span>
                <textarea
                  name="seizedEvidence"
                  value={formData.seizedEvidence || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  rows={2}
                  className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                />
              </div>
              <div>
                <span>
                  (ဃ) ဆေးစာ၊ ကျွမ်းကျင်သူ ထင်မြင်ချက်၊ ဖြောင့်ချက်၊ ကြိုတင်ထွက်ဆိုချက်နှင့်
                  အခြားစာရွက်စာတမ်းများ
                </span>
                <textarea
                  name="medicalEvidence"
                  value={formData.medicalEvidence || ""}
                  onChange={handleChange}
                  disabled={!isEditable}
                  rows={2}
                  className="w-full mt-1 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
                />
              </div>
            </div>
          </div>

          <div>
            <span className="font-bold">၃။ သုံးသပ်ချက်</span>
            <textarea
              name="analysis"
              value={formData.analysis || ""}
              onChange={handleChange}
              disabled={!isEditable}
              rows={3}
              className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
            />
          </div>

          <div>
            <span className="font-bold">
              ၄။ အမှုစစ် ရဲအရာရှိက ထပ်မံစစ်ဆေးဆောင်ရွက်ရန် လိုအပ်ချက်များ (ရှိလျှင်)
            </span>
            <textarea
              name="requirements"
              value={formData.requirements || ""}
              onChange={handleChange}
              disabled={!isEditable}
              rows={2}
              className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
            />
          </div>

          <div>
            <span className="font-bold">၅။ ဥပဒေအကြံပြုချက်</span>
            <textarea
              name="legalAdvice"
              value={formData.legalAdvice || ""}
              onChange={handleChange}
              disabled={!isEditable}
              rows={3}
              className="w-full mt-2 p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none print:border-none print:p-0"
            />
          </div>
        </div>

        {/* Footer Guidelines Block */}
        <div className="text-xs space-y-1 mt-8 pt-4 border-t border-gray-200 text-gray-700 print:text-black">
          <p>
            ၆။ ရည်ညွှန်းချက် (၂) ပါစာဖြင့် ပူးတွဲပေးပို့ထားသော ရည်ညွှန်းချက် (၁) ပါ ရဲစက္ကူ
            အမှုတွဲကို ပူးတွဲ၍ ပြန်လည်ပေးပို့ပါသည်။
          </p>
          <p>၇။ ဤအကြံပြုချက်သည် တရားစွဲအဖွဲ့အစည်းသို့သာ ဥပဒေအရ ပြန်ကြားခြင်းဖြစ်ပါသည်။</p>
          <p>
            ၈။ အထက်အပိုဒ် ၅ ပါ ဥပဒေအကြံပြုချက်အတိုင်း လိုက်နာဆောင်ရွက်ခြင်း ရှိမရှိ
            ပြန်ကြားပေးရန်ဖြစ်ပါသည်။
          </p>
          <p>၉။ ဤအကြံပြုချက်ကို လျှို့ဝှက်အဆင့်သတ်မှတ်ဆောင်ရွက်ရန်ဖြစ်ပါသည်။</p>
        </div>

        {/* Signatures Panel Container */}
        <div className="mt-12 pr-6 flex flex-col items-end space-y-4">
          <div className="w-64 flex flex-col space-y-3">
            {/* လက်မှတ် */}
            <div className="flex items-center">
              <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
              <div
                onClick={() => isEditable && setIsSigning(true)}
                className={`flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center relative group ${
                  isEditable ? "cursor-pointer hover:bg-gray-50 transition" : ""
                }`}
              >
                {formData.signatureImage ? (
                  <>
                    <img
                      src={formData.signatureImage}
                      alt="Signature"
                      className="h-8 object-contain mix-blend-multiply"
                    />
                    {isEditable && (
                      <span className="absolute inset-0 bg-black/40 text-white text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded print:hidden">
                        လက်မှတ်အသစ်ထိုးရန် နှိပ်ပါ
                      </span>
                    )}
                  </>
                ) : (
                  isEditable && (
                    <span className="text-gray-400 text-xs font-sans print:hidden">
                      ✍️ ဒီနေရာတွင် လက်မှတ်ထိုးရန် နှိပ်ပါ
                    </span>
                  )
                )}
              </div>
            </div>

            {/* အမည် */}
            <div className="flex items-center">
              <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
              <input
                type="text"
                name="signName"
                value={formData.signName || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
              />
            </div>

            <div className="text-center text-xs font-bold py-1">တာဝန်ခံဥပဒေအရာရှိ</div>

            {/* ဥပဒေရုံး */}
            <div className="flex items-center">
              <DatalistInput
                name="lawOffice"
                value={formData.lawOffice || ""}
                onChange={handleChange}
                placeholder=""
                options={OFFICE_OPTIONS}
                disabled={!isEditable}
                className="flex-1 bg-transparent border-b border-black focus:outline-none py-0.5 text-center"
              />
              <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ဥပဒေရုံး</span>
            </div>

            {/* ရဲတပ်ဖွဲ့မှူး */}
            <div className="flex items-center">
              <input
                type="text"
                name="policeOfficer"
                value={formData.policeOfficer || ""}
                onChange={handleChange}
                disabled={!isEditable}
                className="flex-1 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
              />
              <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ရဲတပ်ဖွဲ့မှူး</span>
            </div>

            {/* ရဲတပ်ဖွဲ့မှူးရုံး */}
            <div className="flex items-center">
              <DatalistInput
                name="policeOfficerOffice" 
                value={formData.policeoffice || ""}
                onChange={handleChange}
                placeholder=""
                options={OFFICE_OPTIONS}
                disabled={!isEditable}
                className="flex-1 bg-transparent border-b border-black focus:outline-none py-0.5 text-center"
              />
              <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ရဲတပ်ဖွဲ့မှူးရုံး</span>
            </div>

            {/* မြို့ */}
            <div className="flex items-center">
              <DatalistInput
                name="township"
                value={formData.township || ""}
                onChange={handleChange}
                placeholder=""
                options={TOWNSHIP_OPTIONS}
                disabled={!isEditable}
                className="flex-1 text-center bg-transparent border-b border-black focus:outline-none py-0.5"
              />
              <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">မြို့</span>
            </div>
          </div>

          {/* CC Section */}
          <div className="w-full max-w-md mt-4 text-left self-start md:self-auto">
            <span className="text-sm font-medium block mb-1">မိတ္တူကို-</span>
            <textarea
              name="copyTo"
              value={formData.copyTo || ""}
              onChange={handleChange}
              disabled={!isEditable}
              rows={2}
              className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm print:border-none print:p-0"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {/* <FormActionButtons
        isPreview={isPreview}
        isEditable={isEditable}
        isSubmitting={isSubmitting}
        isFormSigned={isFormSigned}
        isApproved={isApproved}
        onCancel={handleBackNavigation}
        onSaveDraft={handleSaveDraft}
        onApproveAndSave={handleApproveAndSave}
        onSubmitToSuperior={handleSubmitToSuperior}
      /> */}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isSigning}
        sigCanvasRef={sigCanvasRef}
        onClose={() => setIsSigning(false)}
        onClear={() => sigCanvasRef.current?.clear()}
        onSave={handleSaveSignature}
      />

      <div className="absolute bottom-4 left-4 text-xs text-gray-400 print:bottom-0 print:left-0">
        လျှို့ဝှက်
      </div>
    </div>
  );
};